import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { DeviceInfo } from "./deviceInfo";

// 指纹生成器类
class FingerprintGenerator {
  private static instance: FingerprintGenerator;
  private readonly FINGERPRINT_KEY = "psa_castlery_device_fingerprint";

  static getInstance(): FingerprintGenerator {
    if (!this.instance) {
      this.instance = new FingerprintGenerator();
    }
    return this.instance;
  }

  /**
   * 从 localStorage 获取缓存的指纹
   */
  private getCachedFingerprintFromStorage(): string | null {
    try {
      return localStorage.getItem(this.FINGERPRINT_KEY);
    } catch (error) {
      console.warn("Failed to get fingerprint from localStorage:", error);
      return null;
    }
  }

  /**
   * 将指纹存储到 localStorage
   */
  private setCachedFingerprintToStorage(fingerprint: string): void {
    try {
      localStorage.setItem(this.FINGERPRINT_KEY, fingerprint);
    } catch (error) {
      console.warn("Failed to save fingerprint to localStorage:", error);
    }
  }

  /**
   * 清除 localStorage 中的缓存
   */
  private clearCacheFromStorage(): void {
    try {
      localStorage.removeItem(this.FINGERPRINT_KEY);
    } catch (error) {
      console.warn("Failed to clear fingerprint from localStorage:", error);
    }
  }

  /**
   * 生成设备指纹
   * @param deviceInfo 设备信息
   * @returns 指纹字符串
   */
  async generateFingerprint(deviceInfo: DeviceInfo): Promise<string> {
    // 先检查 localStorage 中的缓存
    const cachedFingerprint = this.getCachedFingerprintFromStorage();
    if (cachedFingerprint) {
      console.log("🔐 Using cached fingerprint from localStorage");
      return cachedFingerprint;
    }

    try {
      // 初始化 FingerprintJS
      const fp = await FingerprintJS.load();

      // 获取指纹结果
      const result = await fp.get();

      // 结合设备信息生成更完整的指纹
      const combinedFingerprint = this.combineFingerprints(
        result.visitorId,
        deviceInfo
      );

      // 将指纹存储到 localStorage
      this.setCachedFingerprintToStorage(combinedFingerprint);

      console.log("🔐 Device fingerprint generated:", {
        visitorId: result.visitorId,
        confidence: result.confidence.score,
        combinedLength: combinedFingerprint.length,
      });

      return combinedFingerprint;
    } catch (error) {
      console.error("Failed to generate fingerprint:", error);
      // 降级到简单的设备信息指纹
      const fallbackFingerprint = this.generateFallbackFingerprint(deviceInfo);
      // 也将降级指纹存储到 localStorage
      this.setCachedFingerprintToStorage(fallbackFingerprint);
      return fallbackFingerprint;
    }
  }

  /**
   * 结合 FingerprintJS 结果和设备信息生成完整指纹
   */
  private combineFingerprints(
    visitorId: string,
    deviceInfo: DeviceInfo
  ): string {
    const deviceFingerprint = [
      deviceInfo.session.sessionId,
      deviceInfo.platform,
      deviceInfo.language,
      deviceInfo.timezone,
      deviceInfo.deviceType,
      deviceInfo.browser.name,
      deviceInfo.browser.version,
    ].join("|");

    console.log("deviceFingerprint", deviceFingerprint);

    // 将 FingerprintJS 的 visitorId 与设备信息结合
    return `${visitorId}-${this.simpleHash(deviceFingerprint)}`;
  }

  /**
   * 生成降级指纹（当 FingerprintJS 失败时）
   */
  private generateFallbackFingerprint(deviceInfo: DeviceInfo): string {
    const fingerprintData = [
      deviceInfo.userAgent,
      deviceInfo.platform,
      deviceInfo.language,
      deviceInfo.timezone,
      deviceInfo.screenResolution,
      deviceInfo.session.sessionId,
      Date.now().toString(),
    ].join("|");

    return `fallback-${this.simpleHash(fingerprintData)}`;
  }

  /**
   * 简单的哈希函数
   */
  private simpleHash(str: string): string {
    let hash = 0;
    if (str.length === 0) return hash.toString();

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  }

  /**
   * 清除缓存的指纹
   */
  clearCache(): void {
    this.clearCacheFromStorage();
  }

  /**
   * 获取缓存的指纹
   */
  getCachedFingerprint(): string | null {
    return this.getCachedFingerprintFromStorage();
  }
}

// 导出单例实例
export const fingerprintGenerator = FingerprintGenerator.getInstance();

// 便捷函数：生成指纹
export async function generateDeviceFingerprint(
  deviceInfo: DeviceInfo
): Promise<string> {
  return fingerprintGenerator.generateFingerprint(deviceInfo);
}

// 便捷函数：获取缓存的指纹
export function getCachedFingerprint(): string | null {
  return fingerprintGenerator.getCachedFingerprint();
}

// 便捷函数：清除指纹缓存
export function clearFingerprintCache(): void {
  fingerprintGenerator.clearCache();
}
